/**
 * Componente de Lista/Tabela responsiva para Histórico
 * Mobile: Cards | Desktop: Tabela
 */

import React, { useState } from "react";
import {
	Calendar,
	FileText,
	User,
	FolderOpen,
	Copy,
	Star,
	Search,
	ChevronDown,
	ChevronUp,
} from "lucide-react";
import { useNotifications } from "../../feedback/NotificationProvider";
import type { HistoricoItem, TimelineSourceType } from "../../../types/historico";
import { getSourceLabel, getSourceColor } from "../../../utils/historicoNormalizer";

// Fix 5: Mapeamento de status para labels em português
const STATUS_LABELS: Record<string, string> = {
	temp: "Temporário",
	salvo: "Salvo",
	em_edicao: "Em edição",
	finalizado: "Finalizado",
	active: "Ativo",
	inactive: "Inativo",
	processando: "Processando",
};

function getStatusLabel(status: string): string {
	const key = status.toLowerCase();
	if (STATUS_LABELS[key]) return STATUS_LABELS[key];
	return status.charAt(0).toUpperCase() + status.slice(1);
}

interface HistoricoListProps {
	items: HistoricoItem[];
	loading: boolean;
	hasMore: boolean;
	onLoadMore: () => Promise<void>;
}

export function HistoricoList({
	items,
	loading,
	hasMore,
	onLoadMore,
}: HistoricoListProps) {
	const { notify } = useNotifications();
	const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

	const handleCopyPath = async (path: string) => {
		try {
			await navigator.clipboard.writeText(path);
			notify({
				type: "success",
				message: "Caminho copiado para a área de transferência",
			});
		} catch (err) {
			notify({ type: "error", message: "Erro ao copiar caminho" });
		}
	};

	const toggleExpand = (id: string) => {
		setExpandedItems((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const formatDate = (timestamp: string) => {
		try {
			const date = new Date(timestamp);
			return date.toLocaleString("pt-BR", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
				hour: "2-digit",
				minute: "2-digit",
			});
		} catch {
			return timestamp;
		}
	};

	const getStatusColor = (status: string) => {
		const statusLower = status.toLowerCase();
		if (
			statusLower.includes("salvo") ||
			statusLower.includes("finalizado")
		) {
			return "text-blue-400 bg-blue-400/10";
		}
		if (
			statusLower.includes("edição") ||
			statusLower.includes("processando")
		) {
			return "text-yellow-400 bg-yellow-400/10";
		}
		return "text-gray-400 bg-gray-400/10";
	};

	const getSourceBadgeColor = (source: TimelineSourceType) => {
		const color = getSourceColor(source);
		const colorMap = {
			blue: "text-blue-400 bg-blue-400/10",
			green: "text-green-400 bg-green-400/10",
			yellow: "text-yellow-400 bg-yellow-400/10",
			gray: "text-gray-400 bg-gray-400/10",
		};
		return colorMap[color];
	};

	if (items.length === 0 && !loading) {
		return (
			<div className="flex items-center justify-center h-full p-8">
				<div className="text-center">
					<Search className="w-12 h-12 mx-auto mb-4 text-gray-400 opacity-50" />
					<p className="text-sm text-gray-400 font-medium mb-1">Nenhum registro encontrado</p>
					<p className="text-xs text-gray-500">Tente ajustar os filtros de busca</p>
				</div>
			</div>
		);
	}

	return (
		<div className="h-full flex flex-col">
			{/* Mobile: Cards */}
			<div className="block sm:hidden p-3 space-y-3">
				{items.map((item) => {
					const isExpanded = expandedItems.has(item.id);
					return (
						<div
							key={item.id}
							className="bg-navy-800 rounded-lg p-4 border border-navy-700"
						>
							{/* Header do Card */}
							<div className="flex items-start justify-between mb-2">
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2 mb-1">
										<FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
										<span className="text-sm font-medium text-white truncate" title={item.title}>
											{item.title}
										</span>
										{item.favorite && (
											<Star className="w-3.5 h-3.5 text-yellow-400 fill-current flex-shrink-0" />
										)}
									</div>
									<div className="text-xs text-gray-400 truncate">
										{item.subtitle}
									</div>
								</div>
								<button
									onClick={() => toggleExpand(item.id)}
									className="p-1 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-white transition-colors flex-shrink-0"
								>
									{isExpanded ? (
										<ChevronUp className="w-4 h-4" />
									) : (
										<ChevronDown className="w-4 h-4" />
									)}
								</button>
							</div>

							{/* Info básica */}
							<div className="space-y-1.5 text-xs text-gray-400 mb-2">
								<div className="flex items-center gap-1.5">
									<Calendar className="w-3 h-3" />
									<span>{formatDate(item.timestamp)}</span>
								</div>
								<div className="flex items-center gap-1.5">
									<User className="w-3 h-3" />
									<span>{item.userLabel}</span>
								</div>
								<div className="flex items-center gap-1.5 flex-wrap">
									<span
										className={`px-2 py-0.5 rounded text-xs ${getStatusColor(item.status)}`}
									>
										{getStatusLabel(item.status)}
									</span>
									<span className="text-gray-500">•</span>
									<span
										className={`px-2 py-0.5 rounded text-xs ${getSourceBadgeColor(item.source)}`}
									>
										{getSourceLabel(item.source)}
									</span>
								</div>
							</div>

							{/* Expandido: Detalhes */}
							{isExpanded && (
								<div className="mt-3 pt-3 border-t border-navy-700 space-y-2">
									{item.path && (
										<div className="flex items-start gap-2">
											<FolderOpen className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
											<div className="flex-1 min-w-0">
												<div className="text-xs text-gray-500 mb-1">
													Caminho:
												</div>
												<div className="text-xs text-gray-300 break-all font-mono">
													{item.path}
												</div>
											</div>
											<button
												onClick={() =>
													handleCopyPath(item.path)
												}
												className="p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-white transition-colors flex-shrink-0"
												title="Copiar caminho"
											>
												<Copy className="w-3.5 h-3.5" />
											</button>
										</div>
									)}
									{!item.path && (
										<div className="text-xs text-gray-500">
											Caminho não disponível
										</div>
									)}
									{item.meta?.pedido_id && (
										<div className="text-xs text-gray-400">
											<span className="text-gray-500">
												Pedido:
											</span>{" "}
											{item.meta.pedido_id}
										</div>
									)}
									{item.meta?.produto && (
										<div className="text-xs text-gray-400">
											<span className="text-gray-500">
												Produto:
											</span>{" "}
											{item.meta.produto}
										</div>
									)}
								</div>
							)}
						</div>
					);
				})}
			</div>

			{/* Desktop: Tabela */}
			<div className="hidden sm:block h-full flex flex-col">
				<div className="bg-navy-800 rounded-lg overflow-hidden flex-1 flex flex-col">
					{/* Header */}
					<div className="grid grid-cols-12 gap-2 p-3 bg-navy-700 text-xs font-medium text-gray-300 border-b border-navy-600">
						<div className="col-span-2 flex items-center gap-1.5">
							<Calendar className="w-3 h-3" />
							<span>Data/Hora</span>
						</div>
						<div className="col-span-4 flex items-center gap-1.5">
							<FileText className="w-3 h-3" />
							<span>Arquivo</span>
						</div>
						<div className="col-span-2">Origem</div>
						<div className="col-span-1">Cliente</div>
						<div className="col-span-1">Usuário</div>
						<div className="col-span-2">Status</div>
					</div>

					{/* Body */}
					<div className="flex-1 overflow-auto divide-y divide-navy-600">
						{items.map((item) => (
							<div
								key={item.id}
								className="grid grid-cols-12 gap-2 p-3 hover:bg-navy-700/50 transition-colors"
							>
								<div className="col-span-2 text-xs text-gray-300">
									{formatDate(item.timestamp)}
								</div>
								<div className="col-span-4 flex items-center gap-1.5 min-w-0">
									<FileText className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
									<span
										className="text-xs text-white truncate"
										title={item.title}
									>
										{item.title}
									</span>
									{item.favorite && (
										<Star className="w-3 h-3 text-yellow-400 fill-current flex-shrink-0" />
									)}
								</div>
								<div className="col-span-2">
									<span
										className={`px-2 py-0.5 rounded text-xs ${getSourceBadgeColor(item.source)}`}
									>
										{getSourceLabel(item.source)}
									</span>
								</div>
								<div
									className="col-span-1 text-xs text-gray-300 truncate"
									title={item.subtitle}
								>
									{item.subtitle}
								</div>
								<div
									className="col-span-1 text-xs text-gray-300 truncate"
									title={item.userLabel}
								>
									{item.userLabel}
								</div>
								<div className="col-span-2">
									<span
										className={`px-2 py-0.5 rounded text-xs ${getStatusColor(item.status)}`}
									>
										{getStatusLabel(item.status)}
									</span>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Load More Button */}
			{hasMore && (
				<div className="p-3 border-t border-navy-700">
					<button
						onClick={onLoadMore}
						disabled={loading}
						className="w-full px-4 py-2 bg-navy-800 hover:bg-navy-700 text-gray-300 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
					>
						{loading ? (
							<>
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
								<span>Carregando...</span>
							</>
						) : (
							<span>Carregar mais</span>
						)}
					</button>
				</div>
			)}
		</div>
	);
}
